import { type FC, useState } from "react";
import { Form, ListGroup, Spinner } from "react-bootstrap";
import {
	getAllowedObjectivesForProduct,
	getObjectiveRules,
	TIKTOK_ADS_MIN_DAILY_BUDGET,
	TIKTOK_ADS_TOPVIEW_MIN_DAILY_BUDGET,
	TikTokAdsBudgetMode,
	TikTokAdsObjective,
	TikTokAdsProductType,
} from "shared";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import {
	objectiveLabel,
	useTikTokCampaignWizard,
} from "./TikTokCampaignWizardContext";
import { useTikTokAdsRecommendations } from "./useTikTokCampaigns";

const OBJECTIVE_HELP: Record<TikTokAdsObjective, string> = {
	[TikTokAdsObjective.REACH]: "Show your ad to the most people possible.",
	[TikTokAdsObjective.TRAFFIC]: "Send people to your website.",
	[TikTokAdsObjective.VIDEO_VIEWS]: "Maximise views of your video.",
	[TikTokAdsObjective.ENGAGEMENT]: "Not available — requires Spark Ads.",
	[TikTokAdsObjective.LEAD_GENERATION]:
		"Collect contact details via a TikTok Instant Form (page_id).",
	[TikTokAdsObjective.WEB_CONVERSIONS]:
		"Drive purchases/sign-ups with a TikTok Pixel + optimization event.",
};

export const StepTikTokCampaign: FC = () => {
	const { draft, updateDraft, nextStep } = useTikTokCampaignWizard();
	const [error, setError] = useState("");
	const [showTips, setShowTips] = useState(false);
	const recommendations = useTikTokAdsRecommendations(showTips);

	const minBudget =
		draft.productType === TikTokAdsProductType.TOPVIEW
			? TIKTOK_ADS_TOPVIEW_MIN_DAILY_BUDGET
			: TIKTOK_ADS_MIN_DAILY_BUDGET;

	const allowedObjectives = getAllowedObjectivesForProduct(draft.productType);
	const objectiveRules = getObjectiveRules(draft.objective);

	const handleNext = () => {
		if (!draft.name.trim()) {
			setError("Campaign name is required.");
			return;
		}
		if (draft.budgetAmount < minBudget) {
			setError(`Budget must be at least ${minBudget}.`);
			return;
		}
		if (objectiveRules.blockedUntilIntegration) {
			setError(
				objectiveRules.blockReason ??
					"This objective is not available until Instant Form / Pixel are connected.",
			);
			return;
		}
		setError("");
		nextStep();
	};

	const applyRecommendations = () => {
		const data = recommendations.data;
		if (!data) {
			return;
		}
		updateDraft({
			productType: data.productType,
			objective: data.objective,
			budgetAmount: data.budgetAmount,
			ageGroups: data.ageGroups,
			gender: data.gender,
		});
	};

	return (
		<Card>
			<Card.Header>Campaign</Card.Header>
			<Card.Body>
				<div className="mb-3" style={{ display: "flex", gap: 8 }}>
					<Button
						size="sm"
						variant="secondary"
						onClick={() => setShowTips(true)}
						disabled={recommendations.isFetching}
					>
						{recommendations.isFetching ? (
							<>
								<Spinner size="sm" className="me-1" /> Suggesting…
							</>
						) : (
							"Ask Moxie"
						)}
					</Button>
					{recommendations.data && (
						<Button size="sm" onClick={applyRecommendations}>
							Apply suggestions
						</Button>
					)}
				</div>
				{recommendations.data && (
					<>
						<p className="text-secondary mb-2" style={{ fontSize: "0.875rem" }}>
							{recommendations.data.source === "ai"
								? "Suggestions from your marketing plan, ICP, and business goal."
								: (recommendations.data.fallbackReason ??
									"Couldn't reach Moxie AI — showing a default suggestion.")}
						</p>
						<ListGroup className="mb-3">
							{recommendations.data.rationale.map((line) => (
								<ListGroup.Item key={line} style={{ fontSize: "0.875rem" }}>
									{line}
								</ListGroup.Item>
							))}
						</ListGroup>
					</>
				)}

				<Form.Group className="mb-3">
					<Form.Label>Product</Form.Label>
					<Select
						value={draft.productType}
						onChange={(e) =>
							updateDraft({
								productType: e.target.value as TikTokAdsProductType,
							})
						}
					>
						<option value={TikTokAdsProductType.IN_FEED}>
							In-Feed video (auction)
						</option>
						<option value={TikTokAdsProductType.TOPVIEW}>
							TopView (Reservation)
						</option>
					</Select>
					<Form.Text>
						{draft.productType === TikTokAdsProductType.TOPVIEW
							? "Creates a Reach & Frequency campaign (RF_REACH) with TopView inventory. Your advertiser must have TopView enabled."
							: "Standard TikTok In-Feed video ads (auction)."}
					</Form.Text>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Campaign name</Form.Label>
					<Form.Control
						value={draft.name}
						onChange={(e) => updateDraft({ name: e.target.value })}
						placeholder="Spring promotion"
					/>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Objective</Form.Label>
					<Select
						value={draft.objective}
						disabled={draft.productType === TikTokAdsProductType.TOPVIEW}
						onChange={(e) =>
							updateDraft({ objective: e.target.value as TikTokAdsObjective })
						}
					>
						{allowedObjectives.map((objective) => (
							<option key={objective} value={objective}>
								{objectiveLabel(objective)}
								{getObjectiveRules(objective).blockedUntilIntegration
									? " (needs setup)"
									: ""}
							</option>
						))}
					</Select>
					<Form.Text>{OBJECTIVE_HELP[draft.objective]}</Form.Text>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Budget type</Form.Label>
					<Select
						value={draft.budgetMode}
						onChange={(e) =>
							updateDraft({ budgetMode: e.target.value as TikTokAdsBudgetMode })
						}
					>
						<option value={TikTokAdsBudgetMode.DAILY}>Daily budget</option>
						<option value={TikTokAdsBudgetMode.LIFETIME}>
							Lifetime budget
						</option>
					</Select>
					{draft.budgetMode === TikTokAdsBudgetMode.LIFETIME && (
						<Form.Text>A lifetime budget requires an end date.</Form.Text>
					)}
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Budget amount (in your ad account currency)</Form.Label>
					<Form.Control
						type="number"
						min={minBudget}
						step="1"
						value={draft.budgetAmount}
						onChange={(e) =>
							updateDraft({ budgetAmount: Number(e.target.value) })
						}
					/>
					<Form.Text>
						Minimum is {minBudget} per day for this product.
					</Form.Text>
				</Form.Group>

				{error && <p className="text-danger">{error}</p>}

				<Button onClick={handleNext}>Continue</Button>
			</Card.Body>
		</Card>
	);
};
